import { GET_LIVE_CAMPAIGNS, LANGUAGE_QUERY, AUTHORISED_BRAND_QUERY } from '@/constants/GQLConst';
import { GET_CUSTOMER_CODE_CONFIGS_QUERY, GET_SHIP_TO_CODE_QUERY } from '@/constants/customerGQLConst';
import {
    GET_USER_DETAILS_BY_ID,
    GET_USER_DETAILS_BY_USERNAME,
    UPDATE_USER_QUERY,
    UPDATE_USER_BLOCKED_QUERY,
    USER_ACCOUNT_LICENSE_UNLOCK,
    UPDATE_USER_TOUR_STAGE,
    GET_USER_SALESORGS_QUERY,
    GET_ADMIN_USER_DETAILS_BY_ID,
    GET_USER_BLOCKED_STATUS,
    UPDATE_ENABLE_DISABLE_USER_ACCOUNT_QUERY,
    UPDATE_SELECTED_SOLD_TO_AND_SHIP_TO_FOR_ORDERTYPE,
    GET_CUSTOMER_MAPPING,
    EDIT_CUSTOMER_MAPPING
} from '@/constants/userGQLConst';
import {
    ADD_PRINCIPAL_CUTOFF_MUTATION,
    PRINCIPAL_CUTTOFF_QUERY,
    UPDATE_PRINCIPAL_CUTOFF_MUTATION,
    DELETE_PRINCIPAL_MUTATION,
    SUSPENDED_MATERIALS_QUERY,
    CREATE_SUSPENSION_MUTATION,
    UPDATE_SUSPENSION_MUTATION,
    DELETE_SUSPENSION_MUTATION,
    GET_SALES_REP_SUBSTITUTE_PAYMENT_TERMS,
    GET_PRINCIPAL_FOR_SALESORG,
    UPDATE_SALES_REP_SUBSTITUTE_PAYMENT_TERMS,
    UPDATE_SALES_ORG_CONFIG,
    DELETE_SALES_REP_SUBSTITUTE_PAYMENT_TERMS,
    CREATE_CUSTOMER_CODE_CONFIG,
    SEARCH_AD_NAME,
    CREATE_USER_QUERY,
    APL_ADD_PRINCIPAL_CUTOFF_MUTATION,
    GET_CUT_OFF_MANAGEMENT,
    UPDATE_CUSTOMER_CODE_CONFIG,
    UPDATE_PRIVACY_CONTROLS,
    GET_SALES_ORG_CONFIGS_QUERY
} from '@/constants/adminGQLConst';
import { getApolloClient, getApolloClientCampaign, getApolloClientOrder } from './config/apollo-client';
import { getLanguage } from '@/utils/profileUtils';
import {
    GET_USER_DETAILS_BY_ID_MP,
    GET_USER_BLOCKED_STATUS_MP,
    GET_SALES_ORG_CONFIGS_QUERY_MP,
    GET_USER_ORDER_TYPE_INFO
} from '@/constants/marketPlaceGQLConst';
import { UPDATE_SELECTED_ORDERTYPE } from '../constants/userGQLConst';

import { logger } from '@/utils/logger';
/** @type {import('@/services/dashboardType').SuspendedMaterialsApiType} */
export const suspendedMaterialsApi = async (searchVariables, rowsPerPage, rowsIndexStartFrom) => {
    try {
        const variables = {
            language: getLanguage(searchVariables?.language),
            isActive: searchVariables?.isActive,
            first: rowsPerPage,
            after: rowsIndexStartFrom
        };
        if (searchVariables?.searchKey) {
            variables.searchKey = searchVariables?.searchKey;
        }
        if (searchVariables?.fromDate && searchVariables?.toDate) {
            variables.fromDate = searchVariables?.fromDate;
            variables.toDate = searchVariables?.toDate;
        }
        if (searchVariables?.status) {
            variables.status = searchVariables?.status.map((i) => i.value);
        }
        const { data } = await getApolloClient().query({
            query: SUSPENDED_MATERIALS_QUERY,
            variables
        });
        if (data?.getSuspendedMaterials) {
            return data.getSuspendedMaterials;
        }
    } catch (e) {
        logger.info(e);
    }
};

/** @type {import('@/services/dashboardType').CreateOrUpdateSuspensionType} */
export const createSuspension = async (suspensionOrderDetail) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: CREATE_SUSPENSION_MUTATION,
            variables: {
                principalCode: suspensionOrderDetail.principalCode,
                materialNumber: suspensionOrderDetail.materialNumber,
                startDate: suspensionOrderDetail.suspensionStartDate,
                endDate: suspensionOrderDetail.suspensionEndDate
            }
        });
        if (data?.suspendMaterial) {
            return data?.suspendMaterial;
        }
    } catch (e) {
        logger.info({ e });
        throw new Error(e);
    }
};

/** @type {import('@/services/dashboardType').CreateOrUpdateSuspensionType} */
export const updateSuspension = async (suspensionOrderDetail) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: UPDATE_SUSPENSION_MUTATION,
            variables: {
                id: suspensionOrderDetail.id,
                principalCode: suspensionOrderDetail?.principalCode,
                materialNumber: suspensionOrderDetail?.materialNumber,
                startDate: suspensionOrderDetail?.suspensionStartDate,
                endDate: suspensionOrderDetail?.suspensionEndDate
            }
        });
        if (data?.editMaterialSuspension) {
            return data?.editMaterialSuspension;
        }
    } catch (e) {
        logger.info({ e });
        throw new Error(e);
    }
};

/**
 * @param {string | null} id
 * @returns {Promise<Record<string, any> | undefined>}
 */
export const deleteSuspension = async (id) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: DELETE_SUSPENSION_MUTATION,
            variables: {
                id
            }
        });
        if (data?.deleteMaterialSuspension) {
            return data?.deleteMaterialSuspension;
        }
    } catch (e) {
        logger.info({ e });
    }
};

/** @type {import('@/services/dashboardType').PrincipalCuttoffAPIType} */
export const principalCuttoff = async (searchVariables, page) => {
    try {
        const variables = {
            language: getLanguage(searchVariables?.language),
            isActive: searchVariables?.isActive,
            first: page.first,
            after: page.after
        };
        if (searchVariables?.searchKey) {
            variables.searchKey = searchVariables?.searchKey;
        }
        if (searchVariables?.fromDate && searchVariables?.toDate) {
            variables.fromDate = searchVariables?.fromDate;
            variables.toDate = searchVariables?.toDate;
        }
        if (searchVariables?.category?.length) {
            variables.category = searchVariables?.category;
        }
        if (searchVariables?.status?.length) {
            variables.status = searchVariables?.status;
        }
        const { data } = await getApolloClient().query({
            query: PRINCIPAL_CUTTOFF_QUERY,
            variables
        });
        if (data?.getPrincipalCutoffs) {
            return data?.getPrincipalCutoffs;
        }
    } catch (e) {
        logger.info(e);
    }
};

/**
 * @param {import('@/services/dashboardType').OrderCuttOffDetail} orderCuttOffDetail
 * @returns {Promise<Record<string, any> | undefined>}
 */
export const addPrincipalCutoff = async (orderCuttOffDetail) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: ADD_PRINCIPAL_CUTOFF_MUTATION,
            variables: {
                principalCode: orderCuttOffDetail?.principalCode,
                cutoffStartDate: orderCuttOffDetail?.cutoffStartDate,
                cutoffEndDate: orderCuttOffDetail?.cutoffEndDate,
                category: orderCuttOffDetail?.category
            }
        });
        if (data?.cutoffPrincipal) {
            return data?.cutoffPrincipal;
        }
    } catch (e) {
        logger.info({ e });
        throw new Error(e);
    }
};

/**
 * @param {import('@/services/dashboardType').OrderCuttOffDetail} orderCuttOffDetail
 * @param {boolean | null} [isIDMarket]
 * @returns {Promise<Record<string, any> | undefined>}
 */
export const updatePrincipalCutoff = async (orderCuttOffDetail, isIDMarket) => {
    try {
        const variables = {
            isActive: true,
            cutoffStartDate: orderCuttOffDetail?.cutoffStartDate,
            cutoffEndDate: orderCuttOffDetail?.cutoffEndDate,
            category: orderCuttOffDetail?.category
        };
        if (isIDMarket) {
            variables.branch = orderCuttOffDetail?.branchCode;
        }
        const { data } = await getApolloClient().mutate({
            mutation: UPDATE_PRINCIPAL_CUTOFF_MUTATION,
            variables: {
                id: orderCuttOffDetail.id,
                request: variables
            }
        });
        if (data?.updatePrincipalCutoff) {
            return { data: data?.updatePrincipalCutoff };
        }
    } catch (e) {
        logger.info({ e });
        return { errors: e.message };
    }
};

/**
 * @param {string | null} id
 * @returns {Promise<Record<string, any> | undefined>}
 */
export const deletePrincipalCutoff = async (id) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: DELETE_PRINCIPAL_MUTATION,
            variables: {
                id
            }
        });
        if (data?.deletePrincipalCutoff) {
            return data?.deletePrincipalCutoff;
        }
    } catch (e) {
        //if not data show empty (no search found)
        // TODO:  do clean up for error handling
        logger.info({ e });
    }
};

/** @type {import('@/services/dashboardType').GetSalesOrgConfigsAPIType} */
export const getSalesOrgConfigs = async (variables, isMPmarket, temp2faToken) => {
    try {
        const queryObj = {
            query: isMPmarket ? GET_SALES_ORG_CONFIGS_QUERY_MP() : GET_SALES_ORG_CONFIGS_QUERY(),
            variables
        };

        if (temp2faToken) {
            queryObj['context'] = {
                headers: {
                    authorization: `Bearer v2 ${temp2faToken}`
                }
            };
        }
        const { data } = await getApolloClient().query(queryObj);
        if (data?.salesOrgConfigs) {
            return data?.salesOrgConfigs;
        }
    } catch (e) {
        //if not data show empty (no search found)
        // TODO:  do clean up for error handling
        logger.info({ e });
    }
};

/**
 * @param {import('@/services/dashboardType').GetSubstitutePaymenttermsServiceVariables} variables
 * @returns {Promise<import('@/services/dashboardType').GetSubstitutePaymenttermsServiceResponseItem[] | undefined>}
 */
export const getSubstitutePaymenttermsService = async (variables) => {
    try {
        const { data } = await getApolloClientOrder().query({
            query: GET_SALES_REP_SUBSTITUTE_PAYMENT_TERMS,
            variables
        });
        if (data?.salesRepSubstitutePaymentTerm) {
            return data?.salesRepSubstitutePaymentTerm;
        }
    } catch (e) {
        logger.info({ e });
    }
};

/**
 * @param {import('@/services/dashboardType').UpdateSubstitutePaymentTermsVariables} variables
 */
export const updateSubstitutePaymentTerms = async (variables) => {
    try {
        await getApolloClientOrder().mutate({
            mutation: UPDATE_SALES_REP_SUBSTITUTE_PAYMENT_TERMS,
            variables
        });
    } catch (e) {
        logger.info({ e });
    }
};

/**
 * @param {import('@/services/dashboardType').DeleteSubstitutePaymentTermsVariables} variables
 */
export const deleteSubstitutePaymentTerms = async (variables) => {
    try {
        await getApolloClientOrder().mutate({
            mutation: DELETE_SALES_REP_SUBSTITUTE_PAYMENT_TERMS,
            variables
        });
    } catch (e) {
        logger.info({ e });
    }
};

/**
 * @param {import('@/services/dashboardType').GetLanguageServiceVariables} variables
 * @returns {Promise<import('@/services/dashboardType').GetLanguageServiceResponseItem[] | undefined>}
 */
export const getLanguageService = async (variables) => {
    try {
        const { data } = await getApolloClient().query({
            query: LANGUAGE_QUERY,
            variables
        });
        if (data?.languages) {
            return data?.languages;
        }
    } catch (e) {
        logger.info({ e });
    }
};

/**
 * @param {{ salesOrg: string }} variables
 * @returns {Promise<{ principals?: { principalName?: string;principalCode?: string }[] } | undefined>}
 */
export const getPrincipalForSalesOrgService = async (variables) => {
    try {
        const { data } = await getApolloClient().query({
            query: GET_PRINCIPAL_FOR_SALESORG,
            variables
        });
        if (data?.principalForSalesOrg) {
            return data?.principalForSalesOrg;
        }
    } catch (e) {
        logger.info({ e });
    }
};

/**
 * @param {{ salesOrg: string }} variables
 * @returns {Promise<{ principals?: { principalName?: string;principalCode?: string }[] } | undefined>}
 */
export const getPrincipalsDisabledDealsService = async (variables) => {
    try {
        const { data } = await getApolloClient().query({
            query: GET_PRINCIPAL_FOR_SALESORG,
            variables
        });
        if (data?.principalForSalesOrg) {
            return data?.principalForSalesOrg;
        }
    } catch (e) {
        logger.info({ e });
    }
};

/**
 * @param {import('@/services/dashboardType').UpdateSalesOrgConfigVariables} variables
 * @returns {Promise<{ salesOrgConfig?: { id?: string| null } } | undefined>}
 */
export const updateSalesOrgConfig = async (variables) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: UPDATE_SALES_ORG_CONFIG,
            variables
        });
        if (data) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
    }
};

export const getCustCodeConfigs = async () => {
    try {
        const { data } = await getApolloClient().query({
            query: GET_CUSTOMER_CODE_CONFIGS_QUERY,
            fetchPolicy: 'no-cache'
        });
        if (data?.customerCodeConfigs) {
            return data?.customerCodeConfigs;
        }
    } catch (e) {
        logger.info({ e });
    }
};

export const updateCustCodeConfigService = async (variables) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: UPDATE_CUSTOMER_CODE_CONFIG,
            variables
        });
        if (data) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
    }
};

export const createCustCodeConfigService = async (variables) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: CREATE_CUSTOMER_CODE_CONFIG,
            variables
        });
        if (data) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
        throw e;
    }
};

/**
 * @param {import('@/services/dashboardType').UpdateUserService} variables
 */
export const updateUserService = async (variables) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: UPDATE_USER_QUERY,
            variables
        });
        if (data) {
            return data;
        }
    } catch (e) {
        throw e;
    }
};

export const updateEnableDisableUserAccountService = async (variables) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: UPDATE_ENABLE_DISABLE_USER_ACCOUNT_QUERY,
            variables
        });
        if (data) {
            return data;
        }
    } catch (e) {
        throw e;
    }
};

export const updateUserBlockedService = async (variables) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: UPDATE_USER_BLOCKED_QUERY,
            variables
        });
        if (data) {
            return data;
        }
    } catch (e) {
        throw e;
    }
};

export const getUserAccountLicenseService = async (variables) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: USER_ACCOUNT_LICENSE_UNLOCK,
            variables
        });
        if (data) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
        throw e;
    }
};

export const getUserDetailsByIDService = async (variables = {}, isMpMarket = false, temp2FAtoken) => {
    try {
        const queryObj = {
            query: isMpMarket ? GET_USER_DETAILS_BY_ID_MP : GET_USER_DETAILS_BY_ID,
            variables,
            fetchPolicy: 'no-cache'
        };
        if (temp2FAtoken) {
            queryObj.context = {
                headers: {
                    authorization: `Bearer v2 ${temp2FAtoken}`
                }
            };
        }
        const { data } = await getApolloClient().query(queryObj);
        if (data) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
        throw e;
    }
};

export const getUserBlockedStatusService = async (variables, isMpMarket = false) => {
    try {
        const { data } = await getApolloClient().query({
            query: isMpMarket ? GET_USER_BLOCKED_STATUS_MP : GET_USER_BLOCKED_STATUS,
            variables,
            fetchPolicy: 'no-cache'
        });
        if (data) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
        throw e;
    }
};

export const createNewUserService = async (variables) => {
    try {
        const { data, errors } = await getApolloClient().mutate({
            mutation: CREATE_USER_QUERY,
            variables
        });
        if (data && !errors) {
            return data;
        } 
            throw errors;
        
    } catch (e) {
        logger.info({ e });
        throw e;
    }
};

/**
 * @param {{ adName: string }} variables
 */
export const searchADName = async (variables) => {
    try {
        const { data } = await getApolloClient().query({
            query: SEARCH_AD_NAME,
            variables
        });
        if (data) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
        throw e;
    }
};

export const getLiveCampaigns = async (variables, isEnableKRExpansion = false) => {
    try {
        const { data } = await getApolloClientCampaign().query({
            query: GET_LIVE_CAMPAIGNS(isEnableKRExpansion),
            variables
        });
        if (data) {
            return data?.getLiveCampaigns?.data;
        }
    } catch (e) {
        logger.info({ e });
        throw e;
    }
};

/**
 * @param {import('@/services/dashboardType').UpdatePrivacyControlsServiceVariables} variables
 * @returns {Promise<Record<string, any> | undefined>}
 */
export const updatePrivacyControlsService = async (variables) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: UPDATE_PRIVACY_CONTROLS,
            variables
        });
        if (data) {
            return data;
        }
    } catch (e) {
        throw e;
    }
};

export const getAuthorisedBrandName = async (auth) => {
    try {
        const { data } = await getApolloClient().query({
            query: AUTHORISED_BRAND_QUERY(),
            variables: {
                customerCode: auth?.customerCode
            }
        });
        if (data?.getAuthorisedBrands?.authorisedBrands) {
            return data?.getAuthorisedBrands?.authorisedBrands;
        } 
            return { DEFAULT: true };
        
    } catch (e) {
        logger.info(e);
        return { DEFAULT: true };
    }
};

/**
 * @param {{ username?: string; }} variables
 * @returns {Promise<string[]>}
 */
export const getSalesRepData = async (variables) => {
    try {
        const { data } = await getApolloClient().query({
            query: GET_USER_DETAILS_BY_USERNAME,
            variables
        });
        if (data?.salesRepresentativeInfo?.rawMetaData?.uniquePrincipalNumber?.length) {
            return data?.salesRepresentativeInfo?.rawMetaData?.uniquePrincipalNumber;
        } 
            return [];
        
    } catch (e) {
        logger.info({ e });
        return [];
    }
};

/**
 * @param {import('@/services/dashboardType').OrderCuttOffDetail} orderCuttOffDetail
 * @returns {Promise<Record<string, any> | undefined>}
 */
export const aplAddPrincipalCutoff = async (orderCuttOffDetail) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: APL_ADD_PRINCIPAL_CUTOFF_MUTATION,
            variables: {
                principalCode: orderCuttOffDetail?.principalCode,
                cutoffStartDate: orderCuttOffDetail?.cutoffStartDate,
                cutoffEndDate: orderCuttOffDetail?.cutoffEndDate,
                category: orderCuttOffDetail?.category,
                branch: orderCuttOffDetail?.branchCode,
                configurationName: orderCuttOffDetail?.module
            }
        });
        if (data?.cutoffPrincipal) {
            return data?.cutoffPrincipal;
        }
    } catch (e) {
        logger.info({ e });
        throw new Error(e);
    }
};

/**
 * @param {import('@/services/dashboardType').CutOffVariables} variables
 * @returns {Promise<import('@/services/dashboardType').CuttoffAPIResponse>}
 */
export const fetchPaymentCutOff = async (variables) => {
    try {
        const { data } = await getApolloClient().query({
            query: GET_CUT_OFF_MANAGEMENT,
            variables
        });
        if (data?.getPrincipalCutoffs) {
            return data?.getPrincipalCutoffs;
        }
    } catch (e) {
        logger.info(e);
    }
};

export const updateTourStage = async (tourStage) => {
    try {
        getApolloClient().mutate({
            mutation: UPDATE_USER_TOUR_STAGE,
            variables: {
                tourStage
            }
        });
    } catch (e) {
        logger.info({ e });
        throw new Error(e);
    }
};

export const getUserSalesOrgsService = async (variables) => {
    try {
        const { data } = await getApolloClient().query({
            query: GET_USER_SALESORGS_QUERY,
            variables,
            fetchPolicy: 'no-cache'
        });
        if (data) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
        throw e;
    }
};

export const getAdminUserDetailsByIDService = async (variables, isEnableKRExpansion = false) => {
    try {
        const { data } = await getApolloClient().query({
            query: GET_ADMIN_USER_DETAILS_BY_ID(isEnableKRExpansion),
            variables,
            fetchPolicy: 'no-cache'
        });
        if (data) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
        throw e;
    }
};

export const updateSelectedOrderType = async (orderType = '') => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: UPDATE_SELECTED_ORDERTYPE,
            variables: { orderType }
        });

        if (data?.updateSelectedOrderType) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
    }
};

export const updateSelectedSoldToAndShipToForOrderType = async ({ soldTo = '', shipTo = '' }) => {
    try {
        const { data } = await getApolloClient().mutate({
            mutation: UPDATE_SELECTED_SOLD_TO_AND_SHIP_TO_FOR_ORDERTYPE,
            variables: {
                input: {
                    soldTo,
                    shipTo
                }
            }
        });

        if (data?.updateSelectedShipToAddress) {
            return data;
        }
    } catch (e) {
        logger.info({ e });
    }
};

export const getUserOrderTypeInfo = async (variables = {}) => {
    try {
        const { data } = await getApolloClient().query({
            query: GET_USER_ORDER_TYPE_INFO,
            variables,
            fetchPolicy: 'no-cache'
        });
        if (data.user) {
            return data.user;
        }
    } catch (e) {
        logger.info({ e });
        throw e;
    }
};

export const getCustomerMappingService = async (variables) => {
    const { data } = await getApolloClient().query({
        query: GET_CUSTOMER_MAPPING,
        variables,
        fetchPolicy: 'no-cache'
    });
    return data;
};

export const editCustomerMappingService = async (variables) => {
    const { data } = await getApolloClient().mutate({
        mutation: EDIT_CUSTOMER_MAPPING,
        variables
    });
    return data;
};

export const getShipToCodesForSalesOrgs = async (customerCode, salesOrgCodes, filterBlockCustomer) => {
    const results = await Promise.all(
        salesOrgCodes.map((code) =>
            getApolloClient()
                .query({
                    query: GET_SHIP_TO_CODE_QUERY,
                    variables: {
                        customer: customerCode,
                        salesOrganisation: code,
                        filterBlockCustomer
                    }
                })
                .then((res) => ({ code, ok: true, res }))
                .catch(() => ({ code, ok: false }))
        )
    );
    return {
        succeeded: results.filter((r) => r.ok),
        failed: results.filter((r) => !r.ok).map((r) => r.code)
    };
};
